<template>
  <dialog ref="commandPaletteModal" class="modal modal-bottom sm:modal-middle" @close="handleClose">
    <div class="modal-box max-w-2xl">
      <form method="dialog">
        <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
      </form>

      <div class="mb-4">
        <input ref="searchInput" type="text" v-model="searchQuery" placeholder="Search pages..."
          class="input input-bordered w-full" @keydown="handleKeyDown" @keydown.enter.prevent="handleEnter"
          @keydown.escape="handleClose" />
      </div>

      <div class="max-h-96 overflow-y-auto">
        <ul class="menu p-2 rounded-box bg-base-200">
          <li v-if="getRoutesList().length === 0" class="menu-title">
            <span class="text-base-content/60">No results found</span>
          </li>
          <template v-for="(route, index) in getRoutesList()" :key="route.key">
            <li :ref="(el: HTMLElement | null) => setRouteRef(index, el)"
              :class="{ 'ring-2 ring-secondary rounded-md': index === getSelectedIndex() }">
              <NuxtLink :to="route.path" class="flex items-center justify-between w-full" @click="handleRouteClick">
                <div>
                  <div class="font-semibold">{{ route.label }}</div>
                  <div class="text-xs text-base-content/60">{{ route.description }}</div>
                </div>
                <kbd class="kbd kbd-sm" v-if="index === getSelectedIndex()">↵</kbd>
              </NuxtLink>
            </li>
          </template>
        </ul>
      </div>

      <div class="mt-4 flex items-center justify-between text-xs text-base-content/60">
        <div class="flex gap-4">
          <span><kbd class="kbd kbd-xs">↑</kbd> <kbd class="kbd kbd-xs">↓</kbd> Navigate</span>
          <span><kbd class="kbd kbd-xs">↵</kbd> Select</span>
          <span><kbd class="kbd kbd-xs">Esc</kbd> Close</span>
        </div>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="handleClose">close</button>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import { COMPANY_ROUTES } from '~/utils/companyRoutes'

const route = useRoute()
const company = useCompanyData()
const { navigateWithCompanyContext } = useAiNavigation()

interface RouteOption {
  key: string
  label: string
  description: string
  route: string
  path: string
}

const commandPaletteModal = ref<HTMLDialogElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const searchQuery = ref('')
const selectedIndex = ref(0)
const routeRefs = ref<(HTMLElement | null)[]>([])

// Use shared route definitions
const availableRoutes = computed<RouteOption[]>(() => {
  if (!company.value?.public_id) return []
  
  const companyId = company.value.public_id
  
  return COMPANY_ROUTES.map(routeDef => {
    const path = routeDef.route === 'company' 
      ? `/company/${companyId}`
      : `/company/${companyId}/${routeDef.route}`
    
    return {
      key: routeDef.key,
      label: routeDef.label,
      description: routeDef.description,
      route: routeDef.route,
      path
    }
  })
})

const filteredRoutes = computed<RouteOption[]>(() => {
  if (!searchQuery.value.trim()) {
    return availableRoutes.value
  }
  
  const query = searchQuery.value.toLowerCase().trim()
  return availableRoutes.value.filter(route => 
    route.label.toLowerCase().includes(query) ||
    route.description.toLowerCase().includes(query) ||
    route.route.toLowerCase().includes(query)
  )
})

// Use a ref for template to avoid TypeScript issues with computed unwrapping
const routesList = ref<RouteOption[]>([])

watch(filteredRoutes, (newRoutes) => {
  routesList.value = newRoutes
}, { immediate: true })

// Getter function for template to help TypeScript
const getRoutesList = () => routesList.value
const getSelectedIndex = () => selectedIndex.value

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    selectedIndex.value = Math.min(selectedIndex.value + 1, filteredRoutes.value.length - 1)
    scrollToSelected()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
    scrollToSelected()
  } else if (e.key === 'Tab') {
    if (e.shiftKey) {
      e.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      scrollToSelected()
    } else {
      e.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, filteredRoutes.value.length - 1)
      scrollToSelected()
    }
  }
}

const handleEnter = () => {
  if (filteredRoutes.value.length > 0 && selectedIndex.value >= 0) {
    const selectedRoute = filteredRoutes.value[selectedIndex.value]
    navigateWithCompanyContext(selectedRoute.route)
    handleClose()
  }
}

const handleRouteClick = () => {
  handleClose()
}

const setRouteRef = (index: number, el: HTMLElement | null) => {
  if (el) {
    routeRefs.value[index] = el
  }
}

const scrollToSelected = () => {
  nextTick(() => {
    const selectedElement = routeRefs.value[selectedIndex.value]
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  })
}

const handleClose = () => {
  commandPaletteModal.value?.close()
  searchQuery.value = ''
  selectedIndex.value = 0
}

const openModal = () => {
  commandPaletteModal.value?.showModal()
  searchQuery.value = ''
  selectedIndex.value = 0
  nextTick(() => {
    searchInput.value?.focus()
  })
}

const closeModal = () => {
  handleClose()
}

// Reset selected index when search query changes
watch(searchQuery, () => {
  selectedIndex.value = 0
})

defineExpose({
  openModal,
  closeModal
})
</script>

